import type { ForwardedRef} from "react";
import type React from "react"
import { forwardRef, useEffect, useImperativeHandle, useState } from "react"
import { uniqId } from "../../libs"
import { Portal } from "../Portal";
import { CheckMark, Info, Warning } from "../../icons";

export enum ToastDirectionEnum {
    NorthWest = 'nw',
    NorthEast = 'ne',
    SouthEast = 'se',
    SouthWest = 'sw',
}

export enum ToastStatusEnum {
    Success = 'success',
    Error = 'error',
    Warning = 'warning',
    Neutral = 'neutral',
}

export interface ToastMessage {
    message: string
    status: ToastStatusEnum,
    id: string
    remover: (action: (next:() => void) => void) => NodeJS.Timeout
}

export interface ToastProps {
    direction?: ToastDirectionEnum,
}

export interface ToastHandle {
    add: (message: string, status?: ToastStatusEnum, duration?: number) => void
    clear: () => void
    destroy: () => void
}

function getDirectionClassName(direction: ToastDirectionEnum): string {
    switch (direction) {
        case ToastDirectionEnum.NorthWest:
            return 'top-0 left-0'
        case ToastDirectionEnum.NorthEast:
            return 'top-0 right-0'
        case ToastDirectionEnum.SouthEast:
            return 'bottom-0 right-0'
        case ToastDirectionEnum.SouthWest:
            return 'bottom-0 left-0'
    }
}

function getStatusClassName(status: ToastStatusEnum): string {
    switch (status) {
        case ToastStatusEnum.Success:
            return 'toast-status-success'
        case ToastStatusEnum.Error:
            return 'toast-status-error'
        case ToastStatusEnum.Warning:
            return 'toast-status-warning'
        case ToastStatusEnum.Neutral:
            return 'toast-status-neutral'
    }
}

// Refonte « Aurora » : pastille d'icône douce par statut (au lieu de la barre latérale)
function getStatusBadge(status: ToastStatusEnum): [string, React.JSX.Element] {
    switch (status) {
        case ToastStatusEnum.Success:
            return ['bg-emerald-50 text-emerald-600 ring-emerald-600/15 dark:bg-emerald-500/10', <CheckMark key="i" className="w-4 h-4 stroke-current" />]
        case ToastStatusEnum.Error:
            return ['bg-rose-50 text-rose-600 ring-rose-600/15 dark:bg-rose-500/10', <Warning key="i" className="w-4 h-4 fill-current" />]
        case ToastStatusEnum.Warning:
            return ['bg-amber-50 text-amber-600 ring-amber-600/20 dark:bg-amber-500/10', <Warning key="i" className="w-4 h-4 fill-current" />]
        default:
            return ['bg-brand-50 text-brand-600 ring-brand-600/15 dark:bg-color7', <Info key="i" className="w-4 h-4 fill-current" />]
    }
}

function ToastItem({
    id,
    message,
    status,
    remover,
}: ToastMessage): React.JSX.Element {

    const [classname, setClassname] = useState('toast-open')

    useEffect(() => {
        const id = remover((next) => {
            setClassname('toast-close')
            setTimeout(() => {
                next()
            }, 500)
        })

        return () => {
            if(id) clearTimeout(id)
        }
    }, [remover])

    return (
        <div className={`toast-item flex items-start gap-3 p-3 ${classname}`} key={id}>
            <span className={`grid place-items-center w-8 h-8 shrink-0 rounded-xl ring-1 ring-inset ${getStatusBadge(status)[0]}`}>
                { getStatusBadge(status)[1] }
            </span>
            <div className="toast-message flex-1 min-w-0 px-0 py-0 pt-1.5 text-[13px] leading-snug font-medium text-ink dark:text-white">{ message }</div>
        </div>
    )
}

function ToastComponent({
    direction = ToastDirectionEnum.SouthEast
}: ToastProps,
ref: ForwardedRef<ToastHandle> ): React.JSX.Element {

    const directionClass = getDirectionClassName(direction)
    const [element, setElement] = useState<ToastMessage[]>([])

    useImperativeHandle(
        ref,
        () => ({
            add: (message: string, status: ToastStatusEnum = ToastStatusEnum.Neutral, duration = 2000) => {
                const id = uniqId()

                const remover = (callback?: (action: () => void) => void): NodeJS.Timeout => {
                    return setTimeout(() => {
                        const action = (): void => {
                            setElement(stack => {
                                return stack.filter(e => e.id !== id)
                            })
                        }

                        if(callback) {
                            callback(action)
                        } else {
                            action()
                        }

                    }, duration)
                }

                setElement(s => {

                    s.push({ message, status, id, remover})
                    return s
                })
            },
            clear: () => {
                setElement([])
            },
            destroy: () => {
                //document.body.removeChild(rootElement)
            },
            element
        }),
        [element]
    )

    if(typeof element[0]?.message === 'object') {
        // if is native error
        console.error('element', element)
        return null
    }

    return (
        <Portal visible={element?.length > 0}>
            <div className={`${directionClass} absolute toast overflow-hidden max-h-screen pointer-events-none`}>
                {
                    element.map(elm => <ToastItem key={ elm.id } { ...elm } />)
                }
            </div>
        </Portal>
    )
}

const Toast = forwardRef<ToastHandle, ToastProps>(ToastComponent)
Toast.displayName = "Toast"

export { Toast }